/**
 * Las tres superficies del cliente, y las reglas puras que deciden en cuál se
 * está parado. Sin fetch y sin React a propósito: quién PUEDE ver cada una es
 * una pregunta de red (`useSurfaces`), pero en cuál estoy y a dónde lleva cada
 * chip es aritmética de rutas, y eso se prueba sin montar nada.
 *
 * Por qué tres shells y no una barra con veinte enlaces: son tres audiencias
 * distintas usando la misma sesión. El candidato entra a buscar trabajo, el
 * recruiter a llenar una vacante, el operador a revisar que la máquina ande.
 * Una sola barra con las rutas de los tres obliga a cada uno a leer las de los
 * otros dos —que es exactamente lo que pasaba: el operador veía "Hoja de vida"
 * y "Workers" en la misma línea, separados por un hairline.
 */
export type Surface = 'candidate' | 'agency' | 'operator';

interface SurfaceMeta {
  /** Dónde aterriza el chip: la portada de la superficie, no la última ruta. */
  home: string;
  /** El prefijo que la delata en el pathname. '' = todo lo demás. */
  prefix: string;
}

/**
 * Cómo se llama cada superficie en el switcher NO vive acá: es texto de
 * pantalla, y sale de `common.surfaces[surface]` en el idioma activo (una
 * constante de módulo congelaría el idioma con el que cargó la app).
 */
export const SURFACE_META: Record<Surface, SurfaceMeta> = {
  candidate: { home: '/dashboard', prefix: '' },
  agency: { home: '/agency', prefix: '/agency' },
  operator: { home: '/admin', prefix: '/admin' },
};

/** El orden en que se dibujan los chips, de lo propio a lo institucional. */
export const SURFACE_ORDER: Surface[] = ['candidate', 'agency', 'operator'];

/**
 * En qué superficie cae un pathname. El candidato es el default y no tiene
 * prefijo: es la app, las otras dos son consolas montadas encima.
 *
 * El match es por segmento completo (`/agency` o `/agency/...`) y no por
 * `startsWith` pelado, para que una ruta futura como `/agencies-faq` no se lea
 * como la consola.
 */
export function surfaceForPath(pathname: string): Surface {
  for (const surface of SURFACE_ORDER) {
    const { prefix } = SURFACE_META[surface];
    if (!prefix) continue;
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return surface;
  }
  return 'candidate';
}

/**
 * Qué superficies puede ver esta persona. El candidato SIEMPRE está: toda
 * cuenta que entra tiene su panel, incluso la de un operador que no se postuló
 * nunca — si no, un operador sin membresía se quedaría sin ninguna puerta.
 */
export function availableSurfaces(flags: { agency: boolean; operator: boolean }): Surface[] {
  return SURFACE_ORDER.filter(
    (surface) =>
      surface === 'candidate' ||
      (surface === 'agency' && flags.agency) ||
      (surface === 'operator' && flags.operator),
  );
}

/**
 * El switcher aparece sólo cuando hay algo que cambiar. Un chip solitario que
 * dice "Candidato" no es navegación, es ruido con la forma de un control.
 */
export function shouldShowSwitcher(surfaces: Surface[]): boolean {
  return surfaces.length > 1;
}
